import { useQuery } from '@apollo/client/react';
import {
  STAFF_SEARCH_ACCOUNTS,
  STAFF_SEARCH_USERS,
  STAFF_SEARCH_DEVICES,
  STAFF_GET_CUSTOMER,
  STAFF_CUSTOMER_DEVICES,
  STAFF_CUSTOMER_FLEETS,
} from '@scio3/graphql-client';
import type {
  AccountSearchResult,
  DeviceSearchResult,
  StaffCustomer,
  StaffDevice,
  UserSearchResult,
} from '@scio3/shared-types';

interface Fleet {
  _id: string;
  name: string;
  description?: string;
  tags?: string[];
  isDefault: boolean;
  deviceCount: number;
  onlineCount: number;
  staleCount: number;
  offlineCount: number;
  createdAt: string;
  updatedAt: string;
}

interface StaffDeviceConnection {
  items: StaffDevice[];
  total: number;
}

/** Skip until the caller has typed at least 2 chars — matches the backend's own floor. */
const MIN_QUERY_LEN = 2;

export function useStaffSearchAccounts(query: string, limit = 25) {
  const trimmed = query.trim();
  return useQuery<{ staffSearchAccounts: AccountSearchResult[] }>(STAFF_SEARCH_ACCOUNTS, {
    variables: { query: trimmed, limit },
    skip: trimmed.length < MIN_QUERY_LEN,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffSearchUsers(query: string, limit = 25) {
  const trimmed = query.trim();
  return useQuery<{ staffSearchUsers: UserSearchResult[] }>(STAFF_SEARCH_USERS, {
    variables: { query: trimmed, limit },
    skip: trimmed.length < MIN_QUERY_LEN,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffSearchDevices(query: string, limit = 25) {
  const trimmed = query.trim();
  return useQuery<{ staffSearchDevices: DeviceSearchResult[] }>(STAFF_SEARCH_DEVICES, {
    variables: { query: trimmed, limit },
    skip: trimmed.length < MIN_QUERY_LEN,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffGetCustomer(accountId: string | undefined) {
  return useQuery<{ staffGetCustomer: StaffCustomer }>(STAFF_GET_CUSTOMER, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffCustomerDevices(accountId: string | undefined) {
  return useQuery<{ staffCustomerDevices: StaffDeviceConnection }>(STAFF_CUSTOMER_DEVICES, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffCustomerFleets(accountId: string | undefined) {
  return useQuery<{ staffCustomerFleets: Fleet[] }>(STAFF_CUSTOMER_FLEETS, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}
